/**
 * Utility for exporting data to Excel (.xlsx) using lazy loading
 */

const formatDateForExport = (value: unknown): string => {
    if (!value) return '';
    const raw = String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw;
};

/**
 * Filas con los mismos encabezados que la plantilla de clientas, para que el
 * archivo exportado se pueda volver a importar sin ajustes.
 */
export const buildClientExportRows = (clients: any[]) =>
    clients.map((client) => ({
        full_name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        age: client.age ?? '',
        sex: client.sex || '',
        status: client.status || '',
        emergency_contact: client.emergency_contact || '',
        notes: client.notes || '',
    }));

/**
 * Filas con los mismos encabezados que la plantilla de membresías. El teléfono
 * se toma de la clienta porque es la llave usada por la importación.
 */
export const buildMembershipExportRows = (memberships: any[], clients: any[]) => {
    const phoneByClientId = new Map(
        clients.map((client) => [client.client_id, client.phone || ''])
    );

    return memberships.map((membership) => ({
        phone: phoneByClientId.get(membership.client_id) || '',
        plan_name: membership.type || '',
        start_date: formatDateForExport(membership.start_date),
        end_date: formatDateForExport(membership.end_date),
        price: membership.price ?? '',
        status: membership.status || '',
        payment_method: membership.payment_method || '',
        payment_status: membership.payment_status || '',
        sessions_remaining: membership.sessions_remaining ?? '',
    }));
};

export const exportToExcel = async (
    dataSets: Array<{ sheetName: string; data: any[] }>,
    fileName: string = 'GymData_Export'
) => {
    try {
        // Lazy load the xlsx library to avoid bloating the initial bundle
        const XLSX = await import('xlsx');

        // Create a new workbook
        const wb = XLSX.utils.book_new();

        dataSets.forEach((set) => {
            // Convert JSON data to worksheet
            const ws = XLSX.utils.json_to_sheet(set.data);
            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, set.sheetName);
        });

        // Generate blob and trigger download
        const dateStr = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `${fileName}_${dateStr}.xlsx`);

        return { success: true };
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        throw error;
    }
};
